import { useResizeObserver } from '@react-aria/utils';
import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import CollapsedTab from './CollapsedTab';
import styles from './NavigationCollapsibleTabs.module.scss';
import NavigationTabBar, { getNavigationTabs, type NavigationTabBarProps } from '../NavigationTabBar/NavigationTabBar';

interface NavigationCollapsibleTabsProps extends NavigationTabBarProps {
  forceCollapse?: boolean;
  debounceTime?: number;
}

const NavigationCollapsibleTabs = ({
  children,
  onSelect,
  forceCollapse = false,
  debounceTime = 100,
}: NavigationCollapsibleTabsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(forceCollapse ? true : undefined);
  const [minWidth, setMinWidth] = useState(0);

  const computeLayout = useCallback(() => {
    const collapsedTabWidth = collapsedRef.current?.offsetWidth ?? 0;

    if (forceCollapse) {
      setIsCollapsed(true);
      setMinWidth(collapsedTabWidth);
      return;
    }

    const availableSpace = containerRef.current?.offsetWidth ?? 0;
    const navigationTabBarWidth = expandedRef.current?.offsetWidth ?? 0;

    const currentIsCollapsed = navigationTabBarWidth >= availableSpace;

    setIsCollapsed(currentIsCollapsed);
    setMinWidth(collapsedTabWidth);
  }, [forceCollapse]);

  useLayoutEffect(() => {
    computeLayout();
  }, [computeLayout]);

  useResizeObserver({
    ref: containerRef,
    onResize: computeLayout,
  });

  const [debouncedIsCollapsed = isCollapsed, setDebouncedIsCollapsed] = useDebounceValue(isCollapsed, debounceTime);

  useEffect(() => {
    return () => setDebouncedIsCollapsed.cancel();
  }, [setDebouncedIsCollapsed]);

  const tabs = getNavigationTabs(children);

  return (
    <div className={styles.container} role="tablist" ref={containerRef} style={{ minWidth }}>
      <CollapsedTab
        className={clsx(styles.collapsedTab, {
          [styles.hidden!]: !debouncedIsCollapsed,
        })}
        aria-hidden={!debouncedIsCollapsed}
        ref={collapsedRef}
        onSelect={onSelect}
        tabs={tabs}
        defaultString=""
      />
      <NavigationTabBar
        tabBarClassName={clsx(styles.navigationTabBar, {
          [styles.hidden!]: debouncedIsCollapsed,
        })}
        aria-hidden={debouncedIsCollapsed}
        ref={expandedRef}
        onSelect={onSelect}
        variant="boxed"
      >
        {children}
      </NavigationTabBar>
    </div>
  );
};

export default Object.assign(NavigationCollapsibleTabs, {
  Tab: NavigationTabBar.Tab,
});
